export type NavItem = { label: string; href: string };

export const navItems: NavItem[] = [
  { label: "Features", href: "/features" },
  { label: "Install", href: "/install" },
  { label: "Docs", href: "/docs" },
  { label: "Blog", href: "/blog" },
  { label: "Contact", href: "/contact" },
];

export type BlogPost = {
  slug: string;
  title: string;
  description: string;
  date: string;
};

export const blogPosts: BlogPost[] = [
  {
    slug: "self-hosted-formspree-alternative",
    title: "A Self-Hosted Formspree Alternative You Actually Own",
    description:
      "Why teams are moving off hosted form services, and how Submify replaces Formspree with your own Postgres database, a Docker deploy, and an AGPL-3.0 license.",
    date: "2026-07-18",
  },
];

export const legalNavItems: NavItem[] = [
  { label: "Privacy", href: "/privacy" },
  { label: "Terms", href: "/terms" },
  { label: "Accessibility", href: "/accessibility" },
];

export type Feature = {
  title: string;
  description: string;
  icon: "key" | "layout-dashboard" | "file-down" | "send" | "cloud-upload" | "server" | "calendar" | "refresh-cw" | "share-2";
};

export const features: Feature[] = [
  {
    title: "Per-project API keys",
    description:
      "Each project gets its own public/secret key pair. One account key works across every site you embed it on — no per-form setup dance.",
    icon: "key",
  },
  {
    title: "A real dashboard",
    description:
      "Review, search, and bulk-delete submissions per project. Everything is scoped by account and project — nobody else's data is ever visible.",
    icon: "layout-dashboard",
  },
  {
    title: "Export to XLSX or PDF",
    description: "Pull full project exports any time, capped at 5,000 rows per project so you stay in control of storage.",
    icon: "file-down",
  },
  {
    title: "Telegram notifications",
    description: "Optional per-project or account-level Telegram alerts the moment a form is submitted — no email server required.",
    icon: "send",
  },
  {
    title: "Optional S3 uploads",
    description:
      "Connect any S3-compatible bucket (AWS S3, Cloudflare R2, MinIO, Wasabi) per project for presigned file uploads. Submify never stores the file bytes.",
    icon: "cloud-upload",
  },
  {
    title: "Your infrastructure, your data",
    description:
      "Runs as one Docker Compose stack — Postgres, the Go API, the dashboard, and Nginx. No third-party SaaS sits in the request path.",
    icon: "server",
  },
  {
    title: "Booking & availability",
    description:
      "Define event types with your own availability windows and share a public booking link — bookers get a manage/cancel link, no account needed.",
    icon: "calendar",
  },
  {
    title: "Automatic backups & one-click updates",
    description:
      "Point Submify at any S3-compatible bucket and it backs itself up on a schedule (every 3 days, keeping the latest 2) — plus a one-click update button that pulls and restarts the stack for you.",
    icon: "refresh-cw",
  },
  {
    title: "Zulivio CRM sync",
    description: "Push a form submission straight into Zulivio as a lead using your existing Zulivio API key — no Zapier, no polling.",
    icon: "share-2",
  },
];

export type Screenshot = {
  src: string;
  alt: string;
  caption: string;
};

export const screenshots: Screenshot[] = [
  {
    src: "/screenshots/dashboard.webp",
    alt: "Submify dashboard showing projects and recent form submissions",
    caption: "Dashboard — every project's submissions, searchable and scoped to your account",
  },
  {
    src: "/screenshots/submissions.webp",
    alt: "Submify project submissions table with search and export",
    caption: "Open a project’s submissions to review the submitted fields and raw JSON. Download CSV for the displayed page, or use Export for XLSX and PDF.",
  },
  {
    src: "/screenshots/calendar.webp",
    alt: "Submify booking calendar and event type configuration",
    caption: "Booking — event types with your own availability, a public link to share",
  },
  {
    src: "/screenshots/backup-settings.webp",
    alt: "Submify settings screen showing S3 backup configuration and update checker",
    caption: "Backups & updates — connect any S3 bucket, back up on a schedule, update in one click",
  },
];

export type InstallTab = {
  id: "deb" | "linux" | "macos" | "windows" | "docker";
  label: string;
  description: string;
  commands: { label?: string; command: string }[];
  downloads?: [label: string, href: string][];
};

export const installTabs: InstallTab[] = [
  {
    id: "deb",
    label: "Ubuntu / Debian",
    description:
      "Package for Ubuntu 24.04 and Debian 12 or newer, amd64 and arm64, with systemd. It sets up Docker if needed, runs Submify in Docker with a PostgreSQL database and generated secrets, and starts it. Read the script first, or download the .deb and run sudo apt install ./file.deb.",
    commands: [
      {
        label: "One command (downloads the latest release, checks its SHA-256, installs it)",
        command: "curl -fsSL https://submify.nodedr.com/install-linux.sh | sudo bash",
      },
    ],
    downloads: [["Download .deb (amd64)", "/downloads/submify-amd64.deb"], ["Download .deb (arm64)", "/downloads/submify-arm64.deb"]],
  },
  {
    id: "linux",
    label: "Linux",
    description:
      "One command installs Docker Engine and git if either is missing, clones the repo, generates strong random secrets on first run, and brings up the full stack. If you installed Docker from Debian's docker.io package, see the Debian prerequisite steps in the docs before running.",
    commands: [
      {
        label: "One-line installer (installs Docker Engine + git if needed)",
        command: "curl -fsSL https://raw.githubusercontent.com/Raktim94/Submify/main/scripts/quickstart.sh | bash",
      },
    ],
  },
  {
    id: "macos",
    label: "macOS",
    description: "Same one-liner as Linux — it runs in any bash shell, including macOS Terminal.",
    commands: [
      {
        label: "One-line installer (installs Docker Desktop + git via Homebrew if needed)",
        command: "curl -fsSL https://raw.githubusercontent.com/Raktim94/Submify/main/scripts/quickstart.sh | bash",
      },
    ],
  },
  {
    id: "windows",
    label: "Windows",
    description:
      "No Docker Desktop — this installs WSL2 (Windows' own free Linux subsystem) with a real Docker Engine + Docker CLI inside it if needed, plus the native Windows Docker CLI via winget, then starts Submify.",
    commands: [
      {
        label: "One-line installer (run in PowerShell)",
        command: "irm https://raw.githubusercontent.com/Raktim94/Submify/main/scripts/quickstart.ps1 | iex",
      },
    ],
  },
  {
    id: "docker",
    label: "Manual (any OS)",
    description: "Prefer to see exactly what you're running before the installer touches anything? Clone and run Compose yourself.",
    commands: [
      { command: "git clone https://github.com/Raktim94/Submify.git" },
      { command: "cd Submify" },
      { command: "./scripts/compose-up.sh up --build -d" },
    ],
  },
];

export type ArchitectureNode = {
  label: string;
  detail: string;
  icon: "globe" | "route" | "server" | "layout-dashboard" | "database" | "cloud";
};

export const architectureNodes: ArchitectureNode[] = [
  { label: "Browser / client sites", detail: "Any HTML form or fetch() call", icon: "globe" },
  { label: "Nginx — single published port", detail: "Routes /api/* to the Go API, everything else to the dashboard", icon: "route" },
  { label: "Go API (Gin)", detail: "Auth, projects, submissions, exports, rate limiting", icon: "server" },
  { label: "Next.js dashboard", detail: "Review submissions, manage projects and keys", icon: "layout-dashboard" },
  { label: "PostgreSQL", detail: "Single database, rows scoped by user_id / project_id", icon: "database" },
  {
    label: "External S3-compatible storage (optional)",
    detail: "AWS S3, Cloudflare R2, MinIO, Wasabi — per project",
    icon: "cloud",
  },
];

export type FAQItem = { q: string; a: string };

export const faqs: FAQItem[] = [
  {
    q: "Is Submify free?",
    a: "Yes. Submify is released under the AGPL-3.0 license — free to self-host, modify, and use commercially. If you run a modified version as a network service for others, you must share that modified source under the same license.",
  },
  {
    q: "Do I need a server or backend code to use it?",
    a: "No. Once your Submify instance is running, any plain HTML form or fetch() call can POST JSON straight to /api/submit with your project's public key.",
  },
  {
    q: "What do I need to install it?",
    a: "Docker Engine and the Docker Compose v2 plugin. The one-line installer also needs git and a bash shell (native on Linux/macOS, or WSL/Git Bash on Windows).",
  },
  {
    q: "Can multiple people use one instance?",
    a: "Submify is single-tenant: one account per instance. Registration is only open while no account exists yet — after the first account is created, sign-up closes and only that account can sign in.",
  },
  {
    q: "Where does my data go?",
    a: "Into your own PostgreSQL database, in containers you run. Optional file uploads go straight to an S3-compatible bucket you configure — Submify only stores the resulting object key, never the file bytes.",
  },
  {
    q: "Who maintains Submify?",
    a: "Submify is built and maintained by NODEDR INFOTECH PRIVATE LIMITED, led by Raktim Ranjit.",
  },
];

// Ready-to-paste prompt for wiring a website form to a self-hosted Submify instance
// using an AI coding assistant (Cursor, Claude, etc.). Docs page renders it in a copy block.
export const SUBMIFY_FORM_AI_PROMPT = `You are helping me connect an HTML/JS contact form to my self-hosted Submify form backend.

Submify facts:
- Submit endpoint: POST https://<MY-SUBMIFY-HOST>/api/submit
- Auth header: "x-api-key: <PROJECT_PUBLIC_KEY>" (looks like pk_live_...). This key is safe to expose in the browser.
- Request body is JSON: { "data": { ...your form fields... }, "files": [] }
- Success is HTTP 201. On error, the JSON response has an "error" string.
- The project secret key (sk_live_...) is NEVER put in browser code. Use it only server-side to sign the raw body with HMAC-SHA256 and send it in the "x-signature" header.

Please:
1. Build an accessible contact form (name, email, message) with client-side validation and a hidden honeypot field named "gotcha". If the honeypot is filled, silently succeed without sending (it is a bot).
2. Submit with fetch() and AbortSignal.timeout(18000). Show inline success and error states, and disable the button while sending.
3. Prefer posting to a same-origin server route (e.g. a Next.js route handler) that forwards to the Submify endpoint, so the public key stays out of the client bundle and you can add the optional x-signature HMAC. If a server route is not possible, post directly from the browser with only the public key.
4. Read the Submify host and public key from environment variables — do not hard-code them.

After submissions arrive I review and export them in the Submify dashboard, or share a read-only client portal link (https://<MY-SUBMIFY-HOST>/<project-slug>) and its password with the client so they can view and export their own submissions.`;

export const SUBMIFY_BOOKING_AI_PROMPT = `You are helping me add a "book a call" flow to my website using my self-hosted Submify's public calendar booking API.

Submify facts:
- No API key needed for this part — the event-type ID itself is the access control (same trust model as a presigned upload URL).
- SUBMIFY_HOST = "https://<MY-SUBMIFY-HOST>", EVENT_TYPE_ID = "<EVENT-TYPE-ID>" (from Calendar -> Event Types -> Copy Link in my dashboard; the ID is the last URL segment).
- All calls are under SUBMIFY_HOST/api/v1/public, no auth headers, and work cross-origin from the browser out of the box.

API contract:
- GET /event-types/{EVENT_TYPE_ID} -> { id, title, description, duration_minutes, location, timezone }
- GET /event-types/{EVENT_TYPE_ID}/slots -> { slots: [{ start: RFC3339, end: RFC3339 }, ...], timezone }. Only genuinely open slots are returned (weekly hours, buffers, minimum notice, and existing bookings already applied) - render what comes back, don't re-derive availability.
- POST /event-types/{EVENT_TYPE_ID}/bookings, body: { starts_at: RFC3339 (one of the returned slots), attendee_name, attendee_email, attendee_timezone (optional, e.g. Intl.DateTimeFormat().resolvedOptions().timeZone), notes (optional) } -> 201 { booking: {..., manage_token}, manage_url }. Returns 409 if the slot was just taken by someone else (real double-booking prevention at the database level) - on 409, re-fetch slots and ask the visitor to pick again, don't show a generic error.
- GET /bookings/{manage_token}/ics downloads a calendar file - link this as "Add to calendar" after a successful booking.

Please:
1. Match my site's existing design system (colors, spacing, fonts, button/card styles) - don't drop in a visually generic, bolted-on widget.
2. Show duration/location/timezone from the event-type response, not hardcoded values.
3. Group slots by day if there are more than about 15 (a full multi-day flat list is overwhelming) - simple day-tabs or a day-grouped layout is enough.
4. Handle the 409 conflict case explicitly, as described above.
5. After a successful booking, show the confirmed time in the visitor's own timezone, the "Add to calendar" link, and manage_url for reschedule/cancel - not just a bare "success" message.
6. Call SUBMIFY_HOST directly from the browser (CORS is already open) - no server-side proxy route needed unless my codebase's conventions require one for other outbound calls.`;
